import { foldAllExceptRoot } from '@/components/Code/Code.utils';
import { EditableChatInput } from '@/components/EditableChatInput';
import { Message } from '@/components/Message';
import {
  MessageUnionType,
  RunInputSchema,
  RunOutputSchema,
  RunSchema,
} from '@/types/schema';
import {
  addToolCallToMessage,
  getMessageAsSerializedMessage,
  getMessageContent,
  getMessageFields,
  getMessageType,
  getOpenAIRoleFromLangSmithRole,
  isMessageLike,
  maybeGetArrayOfMessages,
  setMessageContent,
  setMessageFields,
  setMessageType,
} from '@/utils/messages';
import { isSerializedConstructor } from '@/utils/serialized';

import { CollapsibleAdditionalFields } from './RunDetailsIO';
import {
  PageDocument,
  RunPageDocumentList,
  asPageDocumentList,
} from './RunPageDocument';
import { RunPageMessages } from './RunPageMessages';

export function RunOutput({
  outputs,
  attachments,
  foldDistractions,
  onEditOutputs,
  runType,
}: {
  outputs: RunOutputSchema | undefined;
  attachments?: RunSchema['outputs_s3_urls'];
  foldDistractions?: { inputs: RunInputSchema | undefined };
  onEditOutputs?: (outputs: RunSchema['outputs']) => void;
  runType?: string;
}) {
  const variant = foldDistractions ? 'conversation' : undefined;
  const isStringPromptValue =
    isSerializedConstructor(outputs) &&
    outputs.id[outputs.id.length - 1] === 'StringPromptValue';
  if (isStringPromptValue) {
    return (
      <Message
        content={outputs.kwargs.text}
        attachments={attachments}
        onEditContent={
          onEditOutputs
            ? (value) => {
                onEditOutputs({
                  ...outputs,
                  kwargs: { ...outputs.kwargs, text: value },
                });
              }
            : undefined
        }
      />
    );
  }

  const isChatPromptValue =
    isSerializedConstructor(outputs) &&
    outputs.id[outputs.id.length - 1] === 'ChatPromptValue';

  if (isChatPromptValue) {
    const handleEdit = (messageSetter, i) =>
      onEditOutputs
        ? (value) => {
            const newMessages = [...outputs.kwargs.messages];
            const newMessage = messageSetter(newMessages[i], value);
            newMessages[i] = newMessage;
            onEditOutputs({
              ...outputs,
              kwargs: { ...outputs.kwargs, messages: newMessages },
            });
          }
        : undefined;

    return onEditOutputs ? (
      <EditableChatInput
        messages={outputs.kwargs.messages}
        setMessages={(input: MessageUnionType[]) => {
          onEditOutputs({
            ...outputs,
            kwargs: { ...outputs.kwargs, messages: input },
          });
        }}
        preserveMessageFormat
        messageFormat="oai"
        debounceUpdate
      />
    ) : (
      outputs.kwargs.messages.map((message, i) => (
        <Message
          key={i}
          role={getMessageType(message) ?? 'HUMAN'}
          content={getMessageContent(message) ?? ''}
          fields={getMessageFields(message)}
          attachments={attachments}
          onEditContent={handleEdit(setMessageContent, i)}
          onEditFields={handleEdit(setMessageFields, i)}
          onChangeRole={handleEdit(setMessageType, i)}
        />
      ))
    );
  }

  const isMessage = getMessageAsSerializedMessage(outputs);

  if (isMessage) {
    const handleEdit = (messageSetter) =>
      onEditOutputs
        ? (value) => {
            const newMessage = messageSetter(
              outputs as MessageUnionType,
              value
            );
            onEditOutputs(newMessage);
          }
        : undefined;
    return (
      <Message
        role={getMessageType(isMessage) ?? 'HUMAN'}
        content={getMessageContent(isMessage) ?? ''}
        fields={getMessageFields(isMessage)}
        attachments={attachments}
        onEditContent={handleEdit(setMessageContent)}
        onEditFields={handleEdit(setMessageFields)}
        onChangeRole={handleEdit(setMessageType)}
      />
    );
  }

  const maybeMessagesArray =
    maybeGetArrayOfMessages(outputs) ??
    (outputs?.output && typeof outputs.output === 'object'
      ? maybeGetArrayOfMessages(outputs.output.messages)
      : null);

  if (maybeMessagesArray) {
    return (
      <RunPageMessages
        messages={maybeMessagesArray}
        attachments={attachments}
        foldDistractions={foldDistractions}
        onEdit={onEditOutputs}
      />
    );
  }

  const documentEntries =
    outputs && typeof outputs === 'object'
      ? Object.keys(outputs)
          .map((key): [string, PageDocument[] | undefined] => [
            key,
            asPageDocumentList(outputs[key]),
          ])
          .filter((key): key is [string, PageDocument[]] => !!key[1])
      : undefined;

  const originalDocuments = documentEntries?.map(([key]) => [
    key,
    outputs?.[key],
  ]);

  const messagesEntries =
    outputs && typeof outputs === 'object'
      ? Object.entries(outputs)
          .map(([key, value]) => {
            const messages = Array.isArray(value)
              ? maybeGetArrayOfMessages(value)
              : null;
            return messages ? [key, messages] : null;
          })
          .filter(
            (entry): entry is [string, MessageUnionType[]] => entry !== null
          )
      : undefined;

  if (
    outputs &&
    ((documentEntries?.length ?? 0) || (messagesEntries?.length ?? 0))
  ) {
    const unprocessedKwargs = Object.fromEntries(
      Object.entries(outputs).filter(
        ([key]) =>
          !documentEntries?.find(([k]) => k === key) &&
          !messagesEntries?.find(([k]) => k === key)
      )
    );

    const showObjectKeys = Object.keys(unprocessedKwargs).length > 0;

    return (
      <div className="flex max-w-full flex-col gap-4">
        {messagesEntries?.map(([key, messages]) => (
          <RunPageMessages
            key={key}
            messages={messages}
            foldDistractions={foldDistractions}
            onEdit={
              onEditOutputs
                ? (value) => {
                    onEditOutputs({ ...outputs, [key]: value });
                  }
                : undefined
            }
          />
        ))}

        {!onEditOutputs &&
          documentEntries?.map(([key, docs]) => (
            <RunPageDocumentList
              key={key}
              objectKey={showObjectKeys ? key : undefined}
              documents={docs}
            />
          ))}

        {showObjectKeys && (
          <div className="flex flex-col gap-2 rounded-lg border border-secondary">
            <CollapsibleAdditionalFields
              kwargs={unprocessedKwargs}
              variant={variant}
              attachments={attachments}
              expanded={runType !== 'llm'}
              onEditKwargs={
                onEditOutputs
                  ? (value) => {
                      // preserve documentEntries and messagesEntries
                      const newOutputs = {
                        ...Object.fromEntries(originalDocuments ?? []),
                        ...Object.fromEntries(messagesEntries ?? []),
                      };
                      // update the edited key
                      onEditOutputs({ ...newOutputs, ...value });
                    }
                  : undefined
              }
            />
          </div>
        )}
      </div>
    );
  }

  const isSingleOutputKey = outputs && Object.keys(outputs).length === 1;
  const singleKey = isSingleOutputKey ? Object.keys(outputs)[0] : undefined;

  const singleMessageOutputValue =
    isSingleOutputKey &&
    singleKey &&
    isMessageLike(outputs[singleKey]) &&
    outputs[singleKey];

  if (singleMessageOutputValue) {
    const handleEdit = (messageSetter) =>
      onEditOutputs
        ? (value) => {
            const newMessage = messageSetter(singleMessageOutputValue, value);
            const canAddToolCall =
              newMessage.role === 'ai' ||
              (newMessage.role &&
                getOpenAIRoleFromLangSmithRole(newMessage.role) ===
                  'assistant');
            if (!canAddToolCall) {
              delete newMessage.tool_calls;
            }
            onEditOutputs({ [singleKey]: newMessage });
          }
        : undefined;
    return (
      <Message
        role={getMessageType(singleMessageOutputValue) ?? 'HUMAN'}
        content={getMessageContent(singleMessageOutputValue) ?? ''}
        fields={getMessageFields(singleMessageOutputValue)}
        attachments={attachments}
        onEditContent={handleEdit(setMessageContent)}
        onEditFields={handleEdit(setMessageFields)}
        onChangeRole={handleEdit(setMessageType)}
        onAddToolCall={
          onEditOutputs
            ? () => {
                const withToolCall = addToolCallToMessage(
                  singleMessageOutputValue,
                  '',
                  '',
                  {
                    param: 'value',
                  }
                );
                onEditOutputs({ [singleKey]: withToolCall });
              }
            : undefined
        }
      />
    );
  }

  // is single message list output key
  if (isSingleOutputKey && singleKey) {
    const maybeMessagesArray = maybeGetArrayOfMessages(outputs[singleKey]);
    if (maybeMessagesArray) {
      return (
        <RunPageMessages
          messages={maybeMessagesArray}
          attachments={attachments}
          foldDistractions={foldDistractions}
          onEdit={onEditOutputs}
        />
      );
    }
  }

  return (
    <Message
      content=""
      kwargs={outputs}
      attachments={attachments}
      onCreateEditor={foldDistractions ? foldAllExceptRoot : undefined}
      variant={variant}
      onEditKwargs={onEditOutputs}
    />
  );
}
